import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { stopPropagation } from '@blocksuite/affine/shared/utils';
import {
  ArrowDownSmallIcon,
  BookIcon,
  ChartIcon,
  LayerIcon,
  PlusIcon,
  PublishIcon,
  SendIcon,
} from '@blocksuite/icons/lit';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';

import type { AINetworkSearchConfig } from '../../type';

// Research mode type
export type ResearchMode = 'auto' | 'explore' | 'analyze' | 'deep-research';

// Mode configuration with icons
const MODE_CONFIG: Record<
  ResearchMode,
  { label: string; icon: () => ReturnType<typeof BookIcon> | null }
> = {
  auto: { label: 'Auto', icon: () => null },
  explore: { label: 'Explore', icon: () => BookIcon() },
  analyze: { label: 'Analyze', icon: () => ChartIcon() },
  'deep-research': { label: 'Deep Research', icon: () => LayerIcon() },
};

export class AIPanelInput extends SignalWatcher(WithDisposable(LitElement)) {
  static override styles = css`
    :host {
      width: 100%;
      padding: 0;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
    }

    .root {
      display: flex;
      flex-direction: column;
      background: var(--affine-background-overlay-panel-color);
      width: 100%;
      box-sizing: border-box;
    }

    .input-row {
      display: flex;
      align-items: flex-start;
      width: 100%;
      min-height: 50px;
      max-height: 150px;
      overflow: hidden;
    }

    /*
     * Icon-only attach button. Pill chrome only appears on hover
     * (Phase 8 polish) so the input feels lighter at rest.
     */
    .plus-button-container {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 8px;
      cursor: pointer;
      border-radius: 999px;
      transition: background-color 0.15s;
      flex-shrink: 0;
      color: var(--affine-icon-color);
    }

    .plus-button-container:hover {
      background-color: var(--affine-hover-color);
    }

    /* Visible pill treatment only when a non-auto mode is selected. */
    .plus-button-container.has-mode {
      background-color: var(--affine-background-secondary-color);
    }

    .plus-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .plus-icon svg {
      width: 20px;
      height: 20px;
      color: var(--affine-icon-color);
    }

    .mode-badge {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      font-weight: 500;
      color: var(--affine-text-primary-color);
      padding-right: 4px;
    }

    .mode-badge-icon {
      display: flex;
      align-items: center;
    }

    .mode-badge-icon svg {
      width: 16px;
      height: 16px;
      color: var(--affine-primary-color);
    }

    .textarea-container {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      width: 100%;
      max-height: 150px;
      overflow: hidden;

      textarea {
        width: 100%;
        height: auto;
        min-height: 22px;
        max-height: 150px;
        border: none;
        outline: none;
        -webkit-box-shadow: none;
        -moz-box-shadow: none;
        box-shadow: none;
        background-color: transparent;
        resize: none;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0px;

        color: var(--affine-text-primary-color);

        /* light/sm */
        font-family: var(--affine-font-family);
        font-size: var(--affine-font-sm);
        font-style: normal;
        font-weight: 400;
        line-height: 22px; /* 157.143% */
      }

      textarea::placeholder {
        color: var(--affine-placeholder-color);
      }

      textarea::-moz-placeholder {
        color: var(--affine-placeholder-color);
      }
    }

    .controls-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 8px 0 0 0;
      width: 100%;
      box-sizing: border-box;
    }

    .controls-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Filled circular send button. Dims to 0.4 when empty. */
    .arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: var(--affine-primary-color, #1e96eb);
      opacity: 0.4;
      cursor: not-allowed;
      transition: opacity 0.15s, transform 0.15s;
    }
    .arrow svg {
      width: 18px;
      height: 18px;
      color: var(--affine-pure-white, #fff);
      display: block;
    }
    .arrow[data-active] {
      opacity: 1;
      cursor: pointer;
    }
    .arrow[data-active]:hover {
      transform: translateY(-1px);
    }
    .network {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 28px;
      min-height: 28px;
      cursor: pointer;
      border-radius: 4px;
    }
    .network svg {
      width: 20px;
      height: 20px;
      color: var(--affine-icon-color, #77757d);
      display: block;
    }
    .network:hover {
      background: var(--affine-hover-color, rgba(0, 0, 0, 0.04));
    }
    .network[data-active='true'] svg {
      color: var(--affine-primary-color, #1e96eb);
    }
    .model-selector {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      cursor: pointer;
      border-radius: 4px;
      background: var(--affine-background-secondary-color);
      transition: background-color 0.2s;
      font-size: var(--affine-font-sm);
      color: var(--affine-text-primary-color);
      white-space: nowrap;
    }
    .model-selector:hover {
      background: var(--affine-hover-color);
    }
    .model-selector svg {
      width: 16px;
      height: 16px;
      color: var(--affine-icon-color);
    }
  `;

  private readonly _onInput = () => {
    this.textarea.style.height = 'auto';
    const scrollHeight = this.textarea.scrollHeight;
    const maxHeight = 150;
    this.textarea.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    // Enable scrolling if content exceeds max height
    this.textarea.style.overflowY =
      scrollHeight > maxHeight ? 'auto' : 'hidden';

    this.onInput?.(this.textarea.value);
    const value = this.textarea.value.trim();
    if (value.length > 0) {
      this._arrow.dataset.active = '';
      this._hasContent = true;
    } else {
      delete this._arrow.dataset.active;
      this._hasContent = false;
    }
  };

  private readonly _onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      this._sendToAI(e);
    }
  };

  private readonly _sendToAI = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const value = this.textarea.value.trim();
    if (value.length === 0) return;

    this.onFinish?.(value);
    this.remove();
  };

  private readonly _toggleNetworkSearch = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const enable = this.networkSearchConfig.enabled.value;
    this.networkSearchConfig.setEnabled(!enable);
  };

  private readonly _onPlusClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    this.onPlusClick?.(e);
  };

  private readonly _onModelClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    this.onModelClick?.(e);
  };

  override render() {
    const modeConfig = this.currentMode
      ? MODE_CONFIG[this.currentMode]
      : MODE_CONFIG['explore'];
    const modeIcon = modeConfig.icon();

    return html`<div class="root">
      <div class="input-row">
        <div class="textarea-container">
          <textarea
            placeholder="Ask anything"
            rows="1"
            @keydown=${this._onKeyDown}
            @input=${this._onInput}
            @pointerdown=${stopPropagation}
            @click=${stopPropagation}
            @dblclick=${stopPropagation}
            @cut=${stopPropagation}
            @copy=${stopPropagation}
            @paste=${stopPropagation}
            @keyup=${stopPropagation}
          ></textarea>
        </div>
      </div>
      <div class="controls-row">
        <div
          class="plus-button-container ${this.showModeBadge ? 'has-mode' : ''}"
          @click=${this._onPlusClick}
          @pointerdown=${stopPropagation}
        >
          <span class="plus-icon">${PlusIcon()}</span>
          ${this.showModeBadge
            ? html`<span class="mode-badge">
                ${modeIcon
                  ? html`<span class="mode-badge-icon">${modeIcon}</span>`
                  : nothing}
                <span>${modeConfig.label}</span>
              </span>`
            : nothing}
        </div>
        <div class="controls-right">
          <div
            class="model-selector"
            @click=${this._onModelClick}
            @pointerdown=${stopPropagation}
          >
            <span>${this.selectedModel || 'Sonnet 4.5'}</span>
            ${ArrowDownSmallIcon()}
          </div>
          ${this.networkSearchConfig.visible.value
            ? html`
                <div
                  class="network"
                  data-active=${!!this.networkSearchConfig.enabled.value}
                  @click=${this._toggleNetworkSearch}
                  @pointerdown=${stopPropagation}
                >
                  ${PublishIcon()}
                  <affine-tooltip .offset=${12}
                    >Toggle Network Search</affine-tooltip
                  >
                </div>
              `
            : nothing}
          <div
            class="arrow"
            @click=${this._sendToAI}
            @pointerdown=${stopPropagation}
          >
            ${SendIcon()}
            ${this._hasContent
              ? html`<affine-tooltip .offset=${12}>Send to AI</affine-tooltip>`
              : nothing}
          </div>
        </div>
      </div>
    </div>`;
  }

  override updated(_changedProperties: Map<PropertyKey, unknown>): void {
    const result = super.updated(_changedProperties);
    const scrollHeight = this.textarea.scrollHeight;
    const maxHeight = 150;
    this.textarea.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    this.textarea.style.overflowY =
      scrollHeight > maxHeight ? 'auto' : 'hidden';
    return result;
  }

  @query('.arrow')
  private accessor _arrow!: HTMLDivElement;

  @state()
  private accessor _hasContent = false;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor onFinish: ((input: string) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onInput: ((input: string) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onPlusClick: ((event: MouseEvent) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor onModelClick: ((event: MouseEvent) => void) | undefined = undefined;

  @property({ attribute: false })
  accessor selectedModel: string | undefined = undefined;

  @property({ attribute: false })
  accessor currentMode: ResearchMode = 'explore';

  @property({ attribute: false })
  accessor showModeBadge: boolean = false;

  @query('textarea')
  accessor textarea!: HTMLTextAreaElement;
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-panel-input': AIPanelInput;
  }
}
